import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Boxes,
  ChevronUp,
  Database,
  KeyRound,
  LogOut,
  Menu,
  Plug,
  Settings,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { Brand } from "@/components/site/brand";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ThemeToggle } from "@/components/theme-toggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { signOut, useSession } from "@/lib/auth-client";
import { useBootstrapScope } from "@/lib/scope";
import { cn } from "@/lib/utils";
import { ProjectSwitcher } from "./ProjectSwitcher";
import { RequireAuth } from "./RequireAuth";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";

const NAV_ITEMS = [
  { to: "/console/integrations", label: "Integrations", icon: Plug },
  { to: "/console/loadouts", label: "Loadouts", icon: Boxes },
  { to: "/console/memory", label: "Memory", icon: Sparkles },
  { to: "/console/vectors", label: "Vectors", icon: Database },
  { to: "/console/credentials", label: "Credentials", icon: KeyRound },
  { to: "/console/oauth-apps", label: "OAuth apps", icon: ShieldCheck },
  { to: "/console/settings", label: "Settings", icon: Settings },
] as const;

function NavItem({
  to,
  label,
  icon: Icon,
  onClick,
}: {
  to: string;
  label: string;
  icon: typeof Plug;
  onClick?: () => void;
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
          isActive
            ? "bg-muted font-medium text-foreground"
            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
        )
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  );
}

function UserMenu({ onNavigate }: { onNavigate?: () => void }) {
  const { data } = useSession();
  const navigate = useNavigate();
  const user = data?.user;
  const initials =
    (user?.name ?? user?.email ?? "?")
      .split(/\s+|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "?";

  async function onSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-md border bg-background px-2 py-2 text-left text-sm transition-colors hover:bg-muted"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-medium text-background">
            {initials}
          </span>
          <span className="min-w-0 flex-1 truncate">
            {user?.name || user?.email || "Account"}
          </span>
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-56">
        <DropdownMenuLabel className="flex flex-col">
          <span className="text-sm">{user?.name || "Account"}</span>
          <span className="text-xs text-muted-foreground">{user?.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            navigate("/console/settings");
            onNavigate?.();
          }}
        >
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SidebarBody({
  onNavigate,
  onClose,
}: {
  onNavigate?: () => void;
  onClose?: () => void;
}) {
  return (
    <>
      <div className="flex h-14 items-center justify-between gap-2 px-5">
        <Brand to="/console" />
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close menu"
            className="-mr-2"
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>
      <Separator />
      <ProjectSwitcher />
      <Separator />
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {NAV_ITEMS.map((item) => (
          <NavItem key={item.to} {...item} onClick={onNavigate} />
        ))}
      </nav>
      <div className="space-y-2 border-t bg-muted/10 p-3">
        <WorkspaceSwitcher />
        <UserMenu onNavigate={onNavigate} />
        <div className="flex items-center justify-between px-1">
          <span className="text-xs text-muted-foreground">
            {__APP_VERSION__}
          </span>
          <ThemeToggle />
        </div>
      </div>
    </>
  );
}

export function ConsoleLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const ready = useBootstrapScope();

  return (
    <RequireAuth>
      {!ready ? (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
        </div>
      ) : (
        <div className="flex min-h-screen bg-background">
          {/* Desktop sidebar */}
          <aside className="sticky top-0 hidden h-screen w-60 flex-col border-r bg-muted/20 lg:flex">
            <SidebarBody />
          </aside>

          {/* Mobile sidebar */}
          {mobileOpen && (
            <div className="fixed inset-0 z-40 lg:hidden">
              <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={() => setMobileOpen(false)}
              />
              <aside className="absolute left-0 top-0 flex h-full w-72 flex-col border-r bg-background shadow-xl">
                <SidebarBody
                  onNavigate={() => setMobileOpen(false)}
                  onClose={() => setMobileOpen(false)}
                />
              </aside>
            </div>
          )}

          <div className="relative flex flex-1 flex-col">
            <Button
              variant="outline"
              size="icon"
              className="fixed left-4 top-4 z-30 shadow-sm lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <main className="flex-1 overflow-auto pt-16 lg:pt-0">
              <Outlet />
            </main>
          </div>
        </div>
      )}
    </RequireAuth>
  );
}
